import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
// IMPORT MapPin, Plus, and new icons from lucide-react
import { Plus, MapPin, Trash2, Edit2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { confirmToast } from '../../utils/confirmToast';

// Custom Instagram Icon Component (no changes)
const InstagramIcon = () => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="15" cy="15" r="15" fill="#FF186D" />
    <path d="M18.9476 7.10535H11.0528C8.87277 7.10535 7.10547 8.87264 7.10547 11.0527V18.9475C7.10547 21.1275 8.87277 22.8948 11.0528 22.8948H18.9476C21.1276 22.8948 22.8949 21.1275 22.8949 18.9475V11.0527C22.8949 8.87264 21.1276 7.10535 18.9476 7.10535Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M18.1576 14.5027C18.255 15.1597 18.1428 15.8307 17.8369 16.4203C17.531 17.0099 17.0469 17.488 16.4536 17.7866C15.8603 18.0853 15.188 18.1892 14.5322 18.0837C13.8764 17.9781 13.2706 17.6685 12.8009 17.1989C12.3312 16.7292 12.0216 16.1234 11.9161 15.4676C11.8106 14.8118 11.9145 14.1394 12.2131 13.5461C12.5118 12.9528 12.9899 12.4688 13.5794 12.1629C14.169 11.8569 14.84 11.7447 15.4971 11.8421C16.1673 11.9415 16.7878 12.2538 17.2668 12.7329C17.7459 13.212 18.0582 13.8325 18.1576 14.5027Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M19.3418 10.658H19.3497" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Custom YouTube Icon Component (no changes)
const YoutubeIcon = () => (
  <svg width="32" height="22" viewBox="0 0 32 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M31.3019 3.49479C32.0039 5.90104 32.0039 11.0573 32.0039 11.0573C32.0039 11.0573 32.0039 16.1562 31.3019 18.6198C30.9509 19.9948 29.8394 21.026 28.4939 21.3698C25.9783 22 16.0332 22 16.0332 22C16.0332 22 6.0295 22 3.51396 21.3698C2.16844 21.026 1.05692 19.9948 0.705917 18.6198C0.00390625 16.1562 0.00390625 11.0573 0.00390625 11.0573C0.00390625 11.0573 0.00390625 5.90104 0.705917 3.49479C1.05692 2.11979 2.16844 1.03125 3.51396 0.6875C6.0295 0 16.0332 0 16.0332 0C16.0332 0 25.9783 0 28.4939 0.6875C29.8394 1.03125 30.9509 2.11979 31.3019 3.49479ZM12.7571 15.6979L21.0642 11.0573L12.7571 6.41667V15.6979Z" fill="#EA252C" />
  </svg>
);

// Custom Facebook Icon Component
const FacebookIcon = () => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="15" cy="15" r="15" fill="#1877F2" />
    <path d="M18.1576 7.10535H11.0528C8.87277 7.10535 7.10547 8.87264 7.10547 11.0527V18.9475C7.10547 21.1275 8.87277 22.8948 11.0528 22.8948H18.9476C21.1276 22.8948 22.8949 21.1275 22.8949 18.9475V11.0527C22.8949 8.87264 21.1276 7.10535 18.9476 7.10535Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M15.0001 18.9475V11.0527" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12.0001 15.0001H18.0001" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Interface includes location and bucket
interface Influencer {
  _id: string;
  name: string;
  image: string;
  category: string;
  instaLink?: string;
  instaFollowers?: number;
  youtubeLink?: string;
  youtubeFollowers?: number;
  facebookLink?: string;
  facebookFollowers?: number;
  location: string;
  isActive?: boolean;
  isFeatured?: boolean;
  bio?: string; // ADDED: bio field
  createdAt?: string;
}

const InfluencersSection: React.FC = () => {
  const API_BASE_URL = import.meta.env.VITE_API_URL?.replace(/\/$/, "");
  const transformImage = (url?: string) => {
    if (!url) return '';
    // Apply Cloudinary thumb transforms if this is a Cloudinary URL
    const isCloudinary = url.includes('res.cloudinary.com') && url.includes('/upload/');
    if (!isCloudinary) return url;
    // Insert a transformation segment: auto format/quality, fill crop, modest size
    return url.replace('/upload/', '/upload/f_auto,q_auto:eco,c_fill,w_360,h_360,dpr_auto/');
  };
  const [influencers, setInfluencers] = useState<Influencer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null); // ADDED: Success message state
  const [isSubmitting, setIsSubmitting] = useState(false); // ADDED: Submitting state

  const [showAddForm, setShowAddForm] = useState(false);
  const [newInfluencer, setNewInfluencer] = useState<Influencer>({
    _id: '', // Will be generated by backend
    name: '',
    image: '',
    category: '',
    instaLink: '',
    instaFollowers: 0,
    youtubeLink: '',
    youtubeFollowers: 0,
    facebookLink: '',
    facebookFollowers: 0,
    location: '',
    bio: '', // ADDED: bio field
    isActive: true,
  });
  const [categorySuggestions, setCategorySuggestions] = useState<string[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  // State for editing an influencer (no longer a modal)
  const [editingInfluencer, setEditingInfluencer] = useState<Influencer | null>(null);
  const [selectedEditFile, setSelectedEditFile] = useState<File | null>(null);

  // Refs for scrolling
  const editFormRef = useRef<HTMLDivElement>(null);
  const addFormRef = useRef<HTMLDivElement>(null);

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const PAGE_SIZE = 8;
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const loadInfluencers = async ({ cursor = null, replace = false }: { cursor?: string | null; replace?: boolean } = {}) => {
    try {
      if (replace) setLoading(true); else setLoadingMore(true);

      const cursorParam = cursor ? `&lastCreatedAt=${encodeURIComponent(cursor)}` : '';
      const response = await fetch(`${API_BASE_URL}/api/influencers?limit=${PAGE_SIZE}${cursorParam}`);
      const data = await response.json();

      if (data.success) {
        const incoming: Influencer[] = Array.isArray(data.influencers) ? data.influencers : [];
        const pagination = data.pagination as { hasMore?: boolean; nextCursor?: string | null } | undefined;

        setInfluencers((prev) => {
          if (replace) return incoming;

          // Create a Map of existing items by ID for efficient lookup
          const existingIds = new Set(prev.map(item => item._id));

          // Filter out incoming items that already exist
          const uniqueIncoming = incoming.filter(item => !existingIds.has(item._id));

          return [...prev, ...uniqueIncoming];
        });

        setHasMore(pagination?.hasMore ?? false);
        setNextCursor(pagination?.nextCursor ?? null);
      } else {
        setError(data.message || 'Failed to fetch influencers');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error('Fetch influencers error:', err);
    } finally {
      if (replace) setLoading(false); else setLoadingMore(false);
    }
  };

  const fetchCategorySuggestions = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/buckets`); // Assuming buckets can be categories
      const data = await response.json();
      if (data.success && Array.isArray(data.buckets)) {
        const names: string[] = data.buckets
          .map((b: { name?: string }) => b?.name)
          .filter((n: unknown): n is string => typeof n === 'string');
        const uniqueSorted = Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
        setCategorySuggestions(uniqueSorted);
      }
    } catch (err) {
      console.error('Fetch category suggestions error:', err);
    }
  };

  // const handleAddInfluencer = async (e: React.FormEvent) => {
  //   e.preventDefault();
  //   try {
  //     const response = await fetch(`${API_BASE_URL}/api/influencers`, {
  //       method: 'POST',
  //       headers: { 'Content-Type': 'application/json' },
  //       body: JSON.stringify(newInfluencer),
  //     });
  //     const data = await response.json();
  //     if (data.success) {
  //       setInfluencers([data.influencer, ...influencers]);
  //       setNewInfluencer({
  //         name: '', image: '', instaFollowers: 0, instaLink: '', youtubeFollowers: 0, youtubeLink: '', location: '', bucket: '',
  //       });
  //       setShowAddForm(false);
  //     } else {
  //       setError(data.message || 'Failed to create influencer');
  //     }
  //   } catch (err) {
  //     setError('Error creating influencer');
  //     console.error('Create influencer error:', err);
  //   }
  // };

  // Replace your existing handleAddInfluencer function

  const handleAddInfluencer = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setIsSubmitting(true); // Set submitting state

    let finalImageUrl = newInfluencer.image;

    // ✅ VALIDATION: Ensure either a file is selected or a URL is entered
    if (selectedFile && newInfluencer.image) {
      setError('Please either upload a file OR paste a URL, not both.');
      setIsSubmitting(false);
      return;
    }

    if (!selectedFile && !newInfluencer.image) {
      setError('You must upload an image or provide an image URL.');
      setIsSubmitting(false);
      return;
    }

    // 1. If a file is selected, upload it first
    if (selectedFile) {
      const formData = new FormData();
      formData.append('image', selectedFile);

      try {
        const uploadRes = await fetch(`${API_BASE_URL}/api/upload`, {
          method: 'POST',
          body: formData,
        });
        const uploadData = await uploadRes.json();

        if (!uploadRes.ok || !uploadData.success) {
          throw new Error(uploadData.message || 'Image upload failed');
        }
        finalImageUrl = uploadData.imageUrl; // Get the URL from the backend
      } catch (err: any) {
        setError(err.message);
        setIsSubmitting(false);
        return; // Stop if upload fails
      }
    }

    // 2. Now, create the influencer with the final image URL
    try {
      const influencerData = { ...newInfluencer, image: finalImageUrl };
      const response = await fetch(`${API_BASE_URL}/api/influencers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(influencerData),
      });
      const data = await response.json();
      if (data.success) {
        setInfluencers([data.influencer, ...influencers]);
        setNewInfluencer({
          _id: '',
          name: '',
          image: '',
          category: '',
          instaLink: '',
          instaFollowers: 0,
          youtubeLink: '',
          youtubeFollowers: 0,
          location: '',
          bio: '',
          isActive: true,
        });
        setSelectedFile(null);
        setShowAddForm(false);
        setSuccessMessage('Influencer created successfully!'); // Set success message
      } else {
        setError(data.message || 'Failed to create influencer');
      }
    } catch (err) {
      setError('Error creating influencer');
      console.error('Create influencer error:', err);
    } finally {
      setIsSubmitting(false); // Reset submitting state
    }
  };

  // ADDED: Function to handle influencer deletion
  const handleDelete = async (id: string) => {
    const confirmed = await confirmToast('Are you sure you want to delete this influencer?', {
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
    if (!confirmed) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/influencers/${id}`, {
        method: 'DELETE',
      });
      const data = await response.json();
      if (data.success) {
        setInfluencers(influencers.filter(inf => inf._id !== id));
        toast.success('Influencer deleted');
      } else {
        setError(data.message || 'Failed to delete influencer');
        toast.error(data.message || 'Failed to delete influencer');
      }
    } catch (err) {
      setError('Error deleting influencer');
      toast.error('Error deleting influencer');
      console.error('Delete influencer error:', err);
    }
  };

  // ADDED: Function to toggle featured status
  const handleToggleFeatured = async (id: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/influencers/${id}/toggle-featured`, {
        method: 'PATCH',
      });
      const data = await response.json();
      if (data.success) {
        setInfluencers((prev) =>
          prev.map((inf) => (inf._id === id ? { ...inf, isFeatured: data.influencer.isFeatured } : inf))
        );
        toast.success(data.message);
      } else {
        setError(data.message || 'Failed to toggle featured status');
        toast.error(data.message || 'Failed to toggle featured status');
      }
    } catch (err) {
      setError('Error toggling featured status');
      toast.error('Error toggling featured status');
      console.error('Toggle featured error:', err);
    }
  };

  // ADDED: Function to handle influencer update
  // const handleUpdateInfluencer = async (e: React.FormEvent) => {
  //   e.preventDefault();
  //   if (!editingInfluencer) return;

  //   try {
  //     const response = await fetch(`${API_BASE_URL}/api/influencers/${editingInfluencer._id}`, {
  //       method: 'PUT',
  //       headers: { 'Content-Type': 'application/json' },
  //       body: JSON.stringify(editingInfluencer),
  //     });
  //     const data = await response.json();
  //     if (data.success) {
  //       setInfluencers(influencers.map(inf => inf._id === editingInfluencer._id ? data.influencer : inf));
  //       closeEditModal();
  //     } else {
  //       setError(data.message || 'Failed to update influencer');
  //     }
  //   } catch (err) {
  //     setError('Error updating influencer');
  //     console.error('Update influencer error:', err);
  //   }
  // };

  // In InfluencersSection.tsx

  const handleUpdateInfluencer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingInfluencer) return;

    setError(null);
    setSuccessMessage(null);
    setIsSubmitting(true); // Set submitting state

    let finalImageUrl = editingInfluencer.image; // Start with the existing image URL

    // 1. Handle new image upload if a file is selected for editing
    if (selectedEditFile) {
      const formData = new FormData();
      formData.append('image', selectedEditFile);

      try {
        const uploadRes = await fetch(`${API_BASE_URL}/api/upload`, {
          method: 'POST',
          body: formData,
        });
        const uploadData = await uploadRes.json();

        if (!uploadData.success) {
          throw new Error(uploadData.message || 'Image upload failed');
        }
        finalImageUrl = uploadData.imageUrl; // Get the NEW URL from the backend
      } catch (err: any) {
        setError(err.message);
        setIsSubmitting(false);
        return; // Stop if the new image upload fails
      }
    }

    // 2. Now, update the influencer with the final image URL (either the old one or the new one)
    try {
      const updatedInfluencerData = { ...editingInfluencer, image: finalImageUrl };

      const response = await fetch(`${API_BASE_URL}/api/influencers/${editingInfluencer._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedInfluencerData),
      });
      const data = await response.json();

      if (data.success) {
        setInfluencers(influencers.map(inf => inf._id === editingInfluencer._id ? data.influencer : inf));
        setEditingInfluencer(null); // Close the inline edit form
        setSelectedEditFile(null); // Clear the selected file after update
        setSuccessMessage('Influencer updated successfully!'); // Set success message
      } else {
        setError(data.message || 'Failed to update influencer');
      }
    } catch (err) {
      setError('Error updating influencer');
      console.error('Update influencer error:', err);
    } finally {
      setIsSubmitting(false); // Reset submitting state
    }
  };


  // Function to open the inline edit form
  const openEditForm = (influencer: Influencer) => {
    setEditingInfluencer(influencer);
    setShowAddForm(false); // Close add form if open
  };

  // Function to close the inline edit form
  const closeEditForm = () => {
    setEditingInfluencer(null);
    setSelectedEditFile(null);
  };

  const handleLoadMore = () => {
    if (loading || loadingMore || !hasMore) return;
    loadInfluencers({ cursor: nextCursor, replace: false });
  };

  useEffect(() => {
    loadInfluencers({ replace: true });
  }, []);

  useEffect(() => {
    if (showAddForm) {
      fetchCategorySuggestions();
    }
  }, [showAddForm]);

  // Scroll to edit form when editing starts
  useEffect(() => {
    if (editingInfluencer && editFormRef.current) {
      editFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [editingInfluencer]);

  // Scroll to add form when it opens
  useEffect(() => {
    if (showAddForm && addFormRef.current) {
      addFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showAddForm]);

  // ADDED: Effect to clear error and success messages after 3 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Auto lazy-load next page when sentinel is near viewport
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    if (!hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !loading && !loadingMore) {
          handleLoadMore();
        }
      },
      { root: null, rootMargin: '400px', threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, nextCursor, influencers.length]);

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
      >
        <div>
          <h1 className="font-poppins text-3xl font-bold text-gray-900">Influencers</h1>
          <p className="font-inter text-gray-600 mt-1">Manage your influencer network</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => {
            setShowAddForm(true);
            setEditingInfluencer(null); // Close edit form if open
          }}
          className="bg-primary text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-primary/90 transition-colors w-full sm:w-auto"
        >
          <Plus className="w-4 h-4" />
          <span className="font-inter font-medium">Add Influencer</span>
        </motion.button>
      </motion.div>

      {/* Add Form */}
      {showAddForm && (
        <motion.div
          ref={addFormRef}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 shadow-xl border border-gray-200"
        >
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
            <div>
              <h3 className="font-poppins text-2xl font-bold text-gray-900">Add New Influencer</h3>
              <p className="font-inter text-sm text-gray-500 mt-1">Fill in the details to add a new influencer</p>
            </div>
            <button
              type="button"
              onClick={() => setShowAddForm(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <form onSubmit={handleAddInfluencer} className="space-y-6">
            {/* Basic Information Section */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h4 className="font-poppins text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <span className="bg-primary/10 text-primary rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm font-bold">1</span>
                Basic Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block font-inter text-sm font-medium text-gray-700 mb-2">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newInfluencer.name}
                    onChange={(e) => setNewInfluencer({ ...newInfluencer, name: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all"
                    placeholder="Enter influencer name"
                    required
                  />
                </div>
                <div>
                  <label className="block font-inter text-sm font-medium text-gray-700 mb-2">
                    Location <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newInfluencer.location}
                    onChange={(e) => setNewInfluencer({ ...newInfluencer, location: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all"
                    placeholder="e.g., New York, USA"
                    required
                  />
                </div>
                <div>
                  <label className="block font-inter text-sm font-medium text-gray-700 mb-2">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    list="category-suggestions"
                    value={newInfluencer.category}
                    onChange={(e) => setNewInfluencer({ ...newInfluencer, category: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all"
                    placeholder="Select or type category"
                    required
                  />
                  <datalist id="category-suggestions">
                    {categorySuggestions.map((name) => (
                      <option key={name} value={name} />
                    ))}
                  </datalist>
                </div>
                <div className="flex items-center pt-8">
                  <input
                    type="checkbox"
                    id="isActiveAdd"
                    checked={newInfluencer.isActive || false}
                    onChange={(e) => setNewInfluencer({ ...newInfluencer, isActive: e.target.checked })}
                    className="mr-3 h-5 w-5 text-primary rounded focus:ring-primary cursor-pointer"
                  />
                  <label htmlFor="isActiveAdd" className="font-inter text-sm font-medium text-gray-700 cursor-pointer">
                    Active Status
                  </label>
                </div>
              </div>
            </div>

            {/* Image Upload Section */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h4 className="font-poppins text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <span className="bg-primary/10 text-primary rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm font-bold">2</span>
                Profile Image
              </h4>
              <div>
                <label className="block font-inter text-sm font-medium text-gray-700 mb-3">
                  Upload or provide image URL
                </label>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <input
                    type="url"
                    placeholder="Paste Image URL"
                    value={newInfluencer.image}
                    onChange={(e) => setNewInfluencer({ ...newInfluencer, image: e.target.value })}
                    className="flex-grow px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all"
                  />
                  <span className="text-center text-gray-500 font-medium">OR</span>
                  <label className="bg-gradient-to-r from-primary to-primary/80 text-white px-6 py-3 rounded-lg hover:from-primary/90 hover:to-primary/70 transition-all font-inter font-medium cursor-pointer text-center shadow-md hover:shadow-lg">
                    Choose File
                    <input
                      type="file"
                      onChange={(e) => {
                        setSelectedFile(e.target.files ? e.target.files[0] : null);
                        setNewInfluencer({ ...newInfluencer, image: '' });
                      }}
                      className="hidden"
                      accept="image/*"
                    />
                  </label>
                </div>
                {selectedFile && (
                  <div className="mt-3 flex items-center space-x-2 text-sm text-green-600 bg-green-50 px-4 py-2 rounded-lg">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>File selected: <strong>{selectedFile.name}</strong></span>
                  </div>
                )}
              </div>
            </div>

            {/* Social Media Links Section */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h4 className="font-poppins text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <span className="bg-primary/10 text-primary rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm font-bold">3</span>
                Social Media Accounts
              </h4>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Instagram */}
                <div className="space-y-3">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg p-2">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                      </svg>
                    </div>
                    <span className="font-semibold text-gray-700">Instagram</span>
                  </div>
                  <input
                    type="url"
                    value={newInfluencer.instaLink || ''}
                    onChange={(e) => setNewInfluencer({ ...newInfluencer, instaLink: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                    placeholder="Instagram profile URL"
                  />
                  <input
                    type="number"
                    value={newInfluencer.instaFollowers || 0}
                    onChange={(e) => setNewInfluencer({ ...newInfluencer, instaFollowers: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                    placeholder="Followers count"
                  />
                </div>

                {/* YouTube */}
                <div className="space-y-3">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg p-2">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                      </svg>
                    </div>
                    <span className="font-semibold text-gray-700">YouTube</span>
                  </div>
                  <input
                    type="url"
                    value={newInfluencer.youtubeLink || ''}
                    onChange={(e) => setNewInfluencer({ ...newInfluencer, youtubeLink: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                    placeholder="YouTube channel URL"
                  />
                  <input
                    type="number"
                    value={newInfluencer.youtubeFollowers || 0}
                    onChange={(e) => setNewInfluencer({ ...newInfluencer, youtubeFollowers: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                    placeholder="Subscribers count"
                  />
                </div>

                {/* Facebook */}
                <div className="space-y-3">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-2">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                      </svg>
                    </div>
                    <span className="font-semibold text-gray-700">Facebook</span>
                  </div>
                  <input
                    type="url"
                    value={newInfluencer.facebookLink || ''}
                    onChange={(e) => setNewInfluencer({ ...newInfluencer, facebookLink: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="Facebook profile URL"
                  />
                  <input
                    type="number"
                    value={newInfluencer.facebookFollowers || 0}
                    onChange={(e) => setNewInfluencer({ ...newInfluencer, facebookFollowers: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="Followers count"
                  />
                </div>
              </div>
            </div>

            {/* Bio Section */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h4 className="font-poppins text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <span className="bg-primary/10 text-primary rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm font-bold">4</span>
                Biography
              </h4>
              <textarea
                value={newInfluencer.bio || ''}
                onChange={(e) => setNewInfluencer({ ...newInfluencer, bio: e.target.value })}
                rows={5}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all resize-none"
                placeholder="Enter a brief biography about the influencer..."
              ></textarea>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <button
                type="submit"
                className="flex-1 bg-gradient-to-r from-primary to-primary/80 text-white px-6 py-4 rounded-lg hover:from-primary/90 hover:to-primary/70 transition-all font-inter font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Adding Influencer...</span>
                  </>
                ) : (
                  <>
                    <Plus className="w-5 h-5" />
                    <span>Add Influencer</span>
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="flex-1 sm:flex-none bg-gray-200 text-gray-700 px-6 py-4 rounded-lg hover:bg-gray-300 transition-colors font-inter font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Inline Edit Form */}
      {editingInfluencer && (
        <motion.div
          ref={editFormRef}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="bg-gradient-to-br from-white to-blue-50 rounded-2xl p-8 shadow-xl border-2 border-blue-200"
        >
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-blue-200">
            <div>
              <h3 className="font-poppins text-2xl font-bold text-gray-900">Edit Influencer</h3>
              <p className="font-inter text-sm text-gray-600 mt-1">Update influencer information for <span className="font-semibold text-primary">{editingInfluencer.name}</span></p>
            </div>
            <button
              type="button"
              onClick={closeEditForm}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <form onSubmit={handleUpdateInfluencer} className="space-y-6">
            {/* Basic Information Section */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h4 className="font-poppins text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <span className="bg-blue-500/10 text-blue-600 rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm font-bold">1</span>
                Basic Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block font-inter text-sm font-medium text-gray-700 mb-2">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editingInfluencer.name}
                    onChange={(e) => setEditingInfluencer({ ...editingInfluencer, name: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    required
                  />
                </div>
                <div>
                  <label className="block font-inter text-sm font-medium text-gray-700 mb-2">
                    Location <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editingInfluencer.location}
                    onChange={(e) => setEditingInfluencer({ ...editingInfluencer, location: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    required
                  />
                </div>
                <div>
                  <label className="block font-inter text-sm font-medium text-gray-700 mb-2">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    list="category-suggestions"
                    value={editingInfluencer.category}
                    onChange={(e) => setEditingInfluencer({ ...editingInfluencer, category: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    required
                  />
                </div>
                <div className="flex items-center pt-8">
                  <input
                    type="checkbox"
                    id="isActiveEdit"
                    checked={editingInfluencer.isActive || false}
                    onChange={(e) => setEditingInfluencer({ ...editingInfluencer, isActive: e.target.checked })}
                    className="mr-3 h-5 w-5 text-blue-600 rounded focus:ring-blue-500 cursor-pointer"
                  />
                  <label htmlFor="isActiveEdit" className="font-inter text-sm font-medium text-gray-700 cursor-pointer">
                    Active Status
                  </label>
                </div>
              </div>
            </div>

            {/* Image Upload Section */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h4 className="font-poppins text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <span className="bg-blue-500/10 text-blue-600 rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm font-bold">2</span>
                Profile Image
              </h4>
              <div>
                <label className="block font-inter text-sm font-medium text-gray-700 mb-3">
                  Update image URL or upload new image
                </label>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <input
                    type="text"
                    placeholder="Paste image URL or relative path"
                    value={editingInfluencer.image}
                    onChange={(e) => setEditingInfluencer({ ...editingInfluencer, image: e.target.value })}
                    className="flex-grow px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                  <span className="text-center text-gray-500 font-medium">OR</span>
                  <label className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-3 rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all font-inter font-medium cursor-pointer text-center shadow-md hover:shadow-lg">
                    Choose File
                    <input
                      type="file"
                      onChange={(e) => setSelectedEditFile(e.target.files ? e.target.files[0] : null)}
                      className="hidden"
                      accept="image/*"
                    />
                  </label>
                </div>
                {selectedEditFile && (
                  <div className="mt-3 flex items-center space-x-2 text-sm text-green-600 bg-green-50 px-4 py-2 rounded-lg">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>File selected: <strong>{selectedEditFile.name}</strong></span>
                  </div>
                )}
              </div>
            </div>

            {/* Biography Section */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h4 className="font-poppins text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <span className="bg-blue-500/10 text-blue-600 rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm font-bold">3</span>
                Biography
              </h4>
              <textarea
                value={editingInfluencer.bio || ''}
                onChange={(e) => setEditingInfluencer({ ...editingInfluencer, bio: e.target.value })}
                rows={5}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none"
                placeholder="Enter a brief biography about the influencer..."
              ></textarea>
            </div>

            {/* Social Media Links Section */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h4 className="font-poppins text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <span className="bg-blue-500/10 text-blue-600 rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm font-bold">4</span>
                Social Media Accounts
              </h4>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Instagram */}
                <div className="space-y-3">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg p-2">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                      </svg>
                    </div>
                    <span className="font-semibold text-gray-700">Instagram</span>
                  </div>
                  <input
                    type="text"
                    value={editingInfluencer.instaLink || ''}
                    onChange={(e) => setEditingInfluencer({ ...editingInfluencer, instaLink: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                    placeholder="Instagram profile URL"
                  />
                  <input
                    type="number"
                    value={editingInfluencer.instaFollowers || 0}
                    onChange={(e) => setEditingInfluencer({ ...editingInfluencer, instaFollowers: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                    placeholder="Followers count"
                  />
                </div>

                {/* YouTube */}
                <div className="space-y-3">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg p-2">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                      </svg>
                    </div>
                    <span className="font-semibold text-gray-700">YouTube</span>
                  </div>
                  <input
                    type="text"
                    value={editingInfluencer.youtubeLink || ''}
                    onChange={(e) => setEditingInfluencer({ ...editingInfluencer, youtubeLink: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                    placeholder="YouTube channel URL"
                  />
                  <input
                    type="number"
                    value={editingInfluencer.youtubeFollowers || 0}
                    onChange={(e) => setEditingInfluencer({ ...editingInfluencer, youtubeFollowers: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                    placeholder="Subscribers count"
                  />
                </div>

                {/* Facebook */}
                <div className="space-y-3">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-2">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                      </svg>
                    </div>
                    <span className="font-semibold text-gray-700">Facebook</span>
                  </div>
                  <input
                    type="text"
                    value={editingInfluencer.facebookLink || ''}
                    onChange={(e) => setEditingInfluencer({ ...editingInfluencer, facebookLink: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="Facebook profile URL"
                  />
                  <input
                    type="number"
                    value={editingInfluencer.facebookFollowers || 0}
                    onChange={(e) => setEditingInfluencer({ ...editingInfluencer, facebookFollowers: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="Followers count"
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <button
                type="submit"
                className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-4 rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all font-inter font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Saving Changes...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Save Changes</span>
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={closeEditForm}
                className="flex-1 sm:flex-none bg-gray-200 text-gray-700 px-6 py-4 rounded-lg hover:bg-gray-300 transition-colors font-inter font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Error Message */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded"
        >
          {error}
          <button onClick={() => setError(null)} className="float-right font-bold text-red-700 hover:text-red-900">×</button>
        </motion.div>
      )}

      {/* Success Message */}
      {successMessage && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded"
        >
          {successMessage}
          <button onClick={() => setSuccessMessage(null)} className="float-right font-bold text-green-700 hover:text-green-900">×</button>
        </motion.div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      )}

      {/* Influencers Grid */}
      {!loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {influencers.map((influencer, index) => (
            // UPDATED: Added relative and group classes for hover effect
            <motion.div
              key={influencer._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              className="bg-[#F3F9FF] rounded-xl p-4 flex flex-col items-start space-y-3 relative group"
            >
              {influencer.isFeatured && (
                <div className="absolute top-2 left-2 bg-yellow-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center space-x-1 z-10">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  <span>Featured</span>
                </div>
              )}
              <div className="relative w-full aspect-square">
                <img
                  src={transformImage(influencer.image)}
                  alt={influencer.name}
                  className="w-full h-full object-cover rounded-lg"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute top-2 right-2 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handleToggleFeatured(influencer._id)}
                    className={`${influencer.isFeatured
                        ? 'bg-yellow-500 hover:bg-yellow-600'
                        : 'bg-gray-400 hover:bg-gray-500'
                      } text-white p-1 rounded transition-colors`}
                    aria-label={influencer.isFeatured ? 'Remove from featured' : 'Add to featured'}
                    title={influencer.isFeatured ? 'Remove from featured' : 'Add to featured'}
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => openEditForm(influencer)}
                    className="bg-blue-500 text-white p-1 rounded hover:bg-blue-600 transition-colors"
                    aria-label="Edit"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(influencer._id)}
                    className="bg-red-500 text-white p-1 rounded hover:bg-red-600 transition-colors"
                    aria-label="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <h3 className="font-sans text-2xl font-semibold text-gray-800 pt-1">{influencer.name}</h3>
              <div className="flex items-center space-x-1.5 text-gray-600">
                <MapPin className="w-4 h-4" /> {/* Adjusted size for consistency */}
                <span className="font-sans text-gray-700 text-sm sm:text-base">{influencer.location}</span>
              </div>
              <div className="space-y-3 w-full pt-2">
                <a
                  href={influencer.instaLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 group/link" // Used group/link to avoid conflict
                >
                  <InstagramIcon />
                  <span className="font-sans text-gray-700 text-sm sm:text-base group-hover/link:text-[#FF186D] transition-colors">
                    {formatNumber(influencer.instaFollowers || 0)} followers
                  </span>
                </a>
                <a
                  href={influencer.youtubeLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 group/link" // Used group/link to avoid conflict
                >
                  <YoutubeIcon />
                  <span className="font-sans text-gray-700 text-sm sm:text-base group-hover/link:text-[#EA252C] transition-colors">
                    {formatNumber(influencer.youtubeFollowers || 0)} followers
                  </span>
                </a>
                <a
                  href={influencer.facebookLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 group/link"
                >
                  <FacebookIcon />
                  <span className="font-sans text-gray-700 text-sm sm:text-base group-hover/link:text-[#1877F2] transition-colors">
                    {formatNumber(influencer.facebookFollowers || 0)} followers
                  </span>
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Load More (fallback) + sentinel for auto-load */}
      {!loading && influencers.length > 0 && (
        <div className="flex flex-col items-center pt-4">
          <div ref={sentinelRef} className="h-1 w-full" aria-hidden="true" />
          <button
            onClick={handleLoadMore}
            disabled={!hasMore || loadingMore}
            className="mt-3 px-6 py-3 rounded-lg bg-gray-900 text-white font-medium shadow hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {loadingMore && (
              <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            <span>{hasMore ? (loadingMore ? 'Loading more...' : 'Load more') : 'No more influencers'}</span>
          </button>
        </div>
      )}

      {/* Empty State */}
      {!loading && influencers.length === 0 && (
        <div className="text-center py-12">
          <p className="font-inter text-gray-500 text-lg">No influencers found. Add your first influencer!</p>
        </div>
      )}
    </div>
  );
};

export default InfluencersSection;
